import React, { useEffect, useRef, useState } from 'react';

let capturedInstallPrompt = null;

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        capturedInstallPrompt = event;
        window.dispatchEvent(new Event('hubtuneinstallpromptavailable'));
    });
    window.addEventListener('appinstalled', () => {
        capturedInstallPrompt = null;
    });
}

const isStandalone = () => (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
);

const InstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showManualInstructions, setShowManualInstructions] = useState(false);
    const reminderTimerRef = useRef(null);
    const fallbackPromptTimerRef = useRef(null);

    const scheduleReminder = () => {
        if (reminderTimerRef.current) {
            window.clearTimeout(reminderTimerRef.current);
        }
        reminderTimerRef.current = window.setTimeout(() => {
            if (!isStandalone()) setShowPrompt(true);
        }, 30000);
    };

    useEffect(() => {
        if (isStandalone()) {
            setIsInstalled(true);
            return undefined;
        }

        fallbackPromptTimerRef.current = window.setTimeout(() => {
            if (!isStandalone()) setShowPrompt(true);
        }, 1500);

        const handleInstallPromptAvailable = () => {
            if (!capturedInstallPrompt) return;
            if (fallbackPromptTimerRef.current) {
                window.clearTimeout(fallbackPromptTimerRef.current);
                fallbackPromptTimerRef.current = null;
            }
            setDeferredPrompt(capturedInstallPrompt);
            setShowPrompt(!isStandalone());
        };

        const handleAppInstalled = () => {
            setIsInstalled(true);
            setShowPrompt(false);
            setDeferredPrompt(null);
            if (reminderTimerRef.current) {
                window.clearTimeout(reminderTimerRef.current);
                reminderTimerRef.current = null;
            }
            setShowManualInstructions(false);
            setShowSuccess(true);

            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                new Notification('HubTune installed', {
                    body: 'HubTune is ready on your home screen. Enjoy your music!',
                    icon: '/logo192.png',
                    tag: 'hubtune-install-success',
                });
            }

            window.setTimeout(() => setShowSuccess(false), 6000);
        };

        handleInstallPromptAvailable();
        window.addEventListener('hubtuneinstallpromptavailable', handleInstallPromptAvailable);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('hubtuneinstallpromptavailable', handleInstallPromptAvailable);
            window.removeEventListener('appinstalled', handleAppInstalled);
            if (reminderTimerRef.current) window.clearTimeout(reminderTimerRef.current);
            if (fallbackPromptTimerRef.current) window.clearTimeout(fallbackPromptTimerRef.current);
        };
    }, []);

    const handleInstallClick = async () => {
        const installPrompt = capturedInstallPrompt || deferredPrompt;
        if (!installPrompt) {
            setShowManualInstructions(true);
            return;
        }

        try {
            await installPrompt.prompt();
            const { outcome } = await installPrompt.userChoice;
            capturedInstallPrompt = null;

            if (outcome === 'accepted') {
                setShowPrompt(false);
                // Some browsers do not emit appinstalled, so retry after 30 seconds.
                scheduleReminder();
            } else {
                setShowPrompt(false);
                scheduleReminder();
            }
        } catch (error) {
            console.error('Error showing install prompt:', error);
            capturedInstallPrompt = null;
            setShowPrompt(false);
            scheduleReminder();
        }

        setDeferredPrompt(null);
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        scheduleReminder();
    };

    return (
        <>
            {!isInstalled && showPrompt && (
                <div className="fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] left-1/2 z-[90] w-[90%] max-w-sm -translate-x-1/2">
                    <div className="rounded-2xl bg-gradient-to-br from-[#1a1a1a] to-[#0e0e0e] p-4 shadow-2xl ring-2 ring-white/20 backdrop-blur-sm">
                        <div className="flex items-start gap-3">
                            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] text-2xl">
                                🎵
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="mb-1 text-base font-bold text-white">Install HubTune</h3>
                                <p className="mb-3 text-sm text-white/70">
                                    Add HubTune to your home screen for quick access.
                                </p>
                                {showManualInstructions && (
                                    <p className="mb-3 rounded-lg bg-white/10 p-3 text-sm text-white/85">
                                        {/iPhone|iPad|iPod/i.test(window.navigator.userAgent)
                                            ? 'In Safari, tap Share, then Add to Home Screen.'
                                            : 'Open your browser menu and choose Install HubTune or Add to Home screen. If that option is missing, try Chrome or Edge.'}
                                    </p>
                                )}
                                <div className="flex gap-2">
                                    <button onClick={handleInstallClick} className="flex-1 rounded-lg bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 active:scale-[0.98]">
                                        {deferredPrompt ? 'Install Now' : 'How to install'}
                                    </button>
                                    <button onClick={handleDismiss} className="rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20 active:scale-[0.98]">
                                        Later
                                    </button>
                                </div>
                            </div>
                            <button onClick={handleDismiss} className="flex-shrink-0 text-xl leading-none text-white/50 hover:text-white" aria-label="Close">
                                ×
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showSuccess && (
                <div className="fixed left-1/2 top-4 z-[110] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 rounded-2xl bg-[#1b1b1b] p-4 text-white shadow-2xl ring-1 ring-green-400/50">
                    <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-green-500 text-xl">✓</div>
                        <div>
                            <p className="font-bold">HubTune installed</p>
                            <p className="text-sm text-white/70">App is ready on your home screen</p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default InstallPrompt;
